import { WS_PAYMENT_ACCESS_KEY } from '@source/constants/common';
import { UploadFile } from 'antd';
import jsCookie from 'js-cookie';

export const getTimeRemaining = (approvalDate: string, validityDays: number) => {
  const approvalTime = new Date(`${approvalDate}Z`).getTime();
  const expirationTime = approvalTime + validityDays * 24 * 60 * 60 * 1000;
  const currentTime = new Date().getTime();
  const timeDifference = expirationTime - currentTime;

  if (timeDifference <= 0) {
    return { isExpired: true, timeRemaining: '' };
  }

  const totalSeconds = Math.floor(timeDifference / 1000);

  const days = Math.floor(totalSeconds / (24 * 3600));
  const remainingSecondsAfterDays = totalSeconds % (24 * 3600);
  const hours = Math.floor(remainingSecondsAfterDays / 3600);
  const remainingSecondsAfterHours = remainingSecondsAfterDays % 3600;
  const minutes = Math.floor(remainingSecondsAfterHours / 60);

  return { isExpired: false, remainingTime: `${days} days ${hours} hours ${minutes} mins` };
};

export const getBase64 = (file: any): Promise<string | ArrayBuffer | null> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file.originFileObj as Blob);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
  });

export interface CurrentDocuments {
  id: number;
  name: string;
  url: string;
  cdn_url: string;
  tags: string[];
}

export const filterDocumentsByTag = (
  documents: CurrentDocuments[] | null | undefined,
  tag: string,
): CurrentDocuments[] => {
  if (!Array.isArray(documents)) {
    throw new Error(`Expected documents to be an array, but got: ${documents}`);
  }

  const filteredDocs = documents.filter((doc) => doc.tags.includes(tag));

  if (
    [
      'nric_passport_front',
      'nric_passport_back',
      'nric_passport',
      'driving_license',
      'driving_license_front',
      'driving_license_back',
    ].includes(tag)
  ) {
    return filteredDocs.length > 0 ? [filteredDocs[filteredDocs.length - 1]] : [];
  }

  return filteredDocs;
};

export const getStatusStyles = (statusName: string) => {
  let color = '';
  let background = '';

  switch (statusName) {
    case 'offer-pending-approve':
      color = '#F90';
      background = '#FFF5E5';
      break;
    case 'offer-approved':
      color = '#59c825';
      background = '#effbe9';
      break;
    default:
      color = '#D93C3F';
      background = '#F7D8D9';
      break;
  }

  return { color, background };
};

export const getStatusName = (carStatusName: string, carDisplayName: string) => {
  if (carStatusName === 'offer-approved') {
    return 'Accepted';
  }
  if (carStatusName !== 'offer-pending-approve' && carStatusName !== 'offer-approved') {
    return 'Unavailable';
  }
  return carDisplayName;
};

export const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  const day = date.getDate();
  const month = date.toLocaleString('default', { month: 'long' });
  const year = date.getFullYear();

  // Get the suffix for the day
  const suffix = (daySelect: number) => {
    if (daySelect > 3 && daySelect < 21) return 'th'; // 4-20
    switch (daySelect % 10) {
      case 1:
        return 'st';
      case 2:
        return 'nd';
      case 3:
        return 'rd';
      default:
        return 'th';
    }
  };

  return `${day}${suffix(day)} ${month} ${year}`;
};

export const defaultUploadOptions = [
  {
    name: 'NRIC / Passport',
    tag: 'nric_passport',
  },
  {
    name: 'NRIC / Passport (Front)',
    tag: 'nric_passport_front',
  },
  {
    name: 'NRIC / Passport (Back)',
    tag: 'nric_passport_back',
  },
  {
    name: 'Driving License',
    tag: 'driving_license',
  },
  {
    name: 'Driving License (Front)',
    tag: 'driving_license_front',
  },
  {
    name: 'Driving License (Back)',
    tag: 'driving_license_back',
  },
  {
    name: 'Copy of Company Registration (SSM)',
    tag: 'company_registration_ssm',
  },
  {
    name: 'Degree or Diploma Certificate',
    tag: 'degree_or_diploma_certificate',
  },
  {
    name: 'EPF Statement',
    tag: 'epf_statement',
  },
  {
    name: 'Payslip',
    tag: 'payslip',
  },
  {
    name: 'Bank Statement',
    tag: 'bank_statement',
  },
  {
    name: 'Job Offer Letter',
    tag: 'job_offer_letter',
  },
  {
    name: 'Passport',
    tag: 'passport',
  },
  {
    name: 'Others',
    tag: 'others',
  },
];

// utils/loanResultParser.ts
export interface LoanResult {
  non_financing?: boolean;
  financing_institution?: string;
  loan_amount?: number;
  loan_tenure?: number;
  interest_rate?: number;
  non_insurance?: boolean;
  insurance_provider?: string;
  insurance_plan_name?: string;
  is_covered_insurance?: boolean | null;
  yearly_premium?: number | null;
  start_date?: string;
  end_date?: string;
  ncd?: number;
  excess?: number;
  policy_number?: string;
  disbursement_date?: string;
  disbursement_amount?: number;
  monthly_installments?: number;
  total_premium_amount?: number;
  add_ons_premium_amount?: number;
  insurance_premium_amount?: number;
  financing_insurance_premium?: number;
  insurance_agent?: string | null;
  transaction?: {
    total_price?: number;
    deposit_amount?: number;
  };
  discount?: number;
}

export const parseLoanResult = (loanResult: LoanResult = {}) => {
  const {
    non_financing: nonFinancing = false,
    financing_institution: financingInstitution = '',
    loan_amount: loanAmount = 0,
    loan_tenure: loanTenure = 0,
    interest_rate: interestRate = 0,
    non_insurance: nonInsurance = false,
    insurance_provider: insuranceProvider = '',
    insurance_plan_name: insurancePlanName = '',
    is_covered_insurance: isCoveredInsurance = null,
    yearly_premium: yearlyPremium = null,
    start_date: startDate = '',
    end_date: endDate = '',
    ncd = 0,
    excess = 0,
    policy_number: policyNumber = '',
    disbursement_date: disbursementDate = '',
    disbursement_amount: disbursementAmount = 0,
    monthly_installments: monthlyInstallments = 0,
    total_premium_amount: totalPremiumAmount = 0,
    add_ons_premium_amount: addOnsPremiumAmount = 0,
    insurance_premium_amount: insurancePremiumAmount = 0,
    financing_insurance_premium: financingInsurancePremium = 0,
    insurance_agent: insuranceAgent = null,
    transaction = {},
    discount = 0,
  } = loanResult || {};

  const { total_price: totalPrice = 0, deposit_amount: depositAmount = 0 } = transaction || {};

  return {
    nonFinancing,
    financingInstitution,
    loanAmount,
    loanTenure,
    interestRate,
    nonInsurance,
    insuranceProvider,
    insurancePlanName,
    isCoveredInsurance,
    yearlyPremium,
    startDate,
    endDate,
    ncd,
    excess,
    policyNumber,
    disbursementDate,
    disbursementAmount,
    monthlyInstallments,
    totalPremiumAmount,
    addOnsPremiumAmount,
    insurancePremiumAmount,
    financingInsurancePremium,
    insuranceAgent,
    totalPrice,
    depositAmount,
    discount,
  };
};

export const parsePaymentSummary = (paymentSummary: any = {}) => {
  const {
    car_price: carPrice = 0,
    insurance_premium: insurancePremium = 0,
    booking_fee_paid: bookingFeePaid = 0,
    booking_fee_payment_method: bookingFeePaymentMethod = '',
    discount = 0,
    loan_amount: loanAmount = 0,
    loan_description: loanDescription = '',
    payments = [],
    balance_amount_payable: balanceAmountPayable = 0,
    due_date: dueDate = '',
  } = paymentSummary || {};

  return {
    carPrice,
    insurancePremium,
    bookingFeePaid,
    bookingFeePaymentMethod,
    discount,
    loanAmount,
    loanDescription,
    payments,
    balanceAmountPayable,
    dueDate,
  };
};

export const getTimeZone = (country: string) => {
  switch (country) {
    case 'TH':
      return 'Asia/Bangkok';
    case 'ID':
      return 'Asia/Jakarta';
    case 'SG':
      return 'Asia/Singapore';
    case 'MY':
      return 'Asia/Kuala_Lumpur';
    case 'JP':
      return 'Asia/Tokyo'; // corrected here
    default:
      return 'Asia/Singapore';
  }
};

export const setPaymentAccessKeyToCookie = (transactionId: string | number, phone: string) => {
  jsCookie.set(`${WS_PAYMENT_ACCESS_KEY}_${transactionId}`, phone, {
    expires: 1,
  });
};

export const getPaymentAccessKeyFromCookie = (transactionId: string | number): string | null => {
  return jsCookie.get(`${WS_PAYMENT_ACCESS_KEY}_${transactionId}`) || null;
};

export const removePaymentAccessKeyFromCookie = (transactionId: string | number) => {
  jsCookie.remove(`${WS_PAYMENT_ACCESS_KEY}_${transactionId}`);
};
